import React from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { Close, CheckCircle, Info, Warning } from '@mui/icons-material';

interface Notification {
  id: string;
  message: string;
  type: string;
}

interface FloatingNotificationsProps {
  notifications: Notification[];
}

const NotificationsContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1100,
  maxWidth: '400px',
}));

const NotificationItem = styled(Paper)<{ type: string }>(({ theme, type }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.spacing(1),
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  background: type === 'success' 
    ? 'rgba(0, 204, 153, 0.2)' 
    : type === 'error' 
      ? 'rgba(244, 67, 54, 0.2)' 
      : 'rgba(138, 43, 226, 0.2)',
  border: `1px solid ${
    type === 'success' 
      ? 'rgba(0, 204, 153, 0.5)' 
      : type === 'error' 
        ? 'rgba(244, 67, 54, 0.5)' 
        : 'rgba(138, 43, 226, 0.5)'
  }`,
}));

const NotificationIcon = styled(Box)<{ type: string }>(({ theme, type }) => ({
  marginRight: theme.spacing(1.5),
  color: type === 'success' 
    ? '#00CC99' 
    : type === 'error' 
      ? '#F44336' 
      : '#8A2BE2',
}));

const NotificationMessage = styled(Typography)(({ theme }) => ({
  flex: 1,
  color: '#fff',
  fontSize: '0.9rem',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    color: '#fff',
  },
}));

const FloatingNotifications: React.FC<FloatingNotificationsProps> = ({ notifications }) => {
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <Warning />;
      default:
        return <Info />;
    }
  };

  return (
    <NotificationsContainer>
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.3 }}
          >
            <NotificationItem type={notification.type}>
              <NotificationIcon type={notification.type}>
                {getNotificationIcon(notification.type)}
              </NotificationIcon>
              <NotificationMessage>
                {notification.message}
              </NotificationMessage>
              <CloseButton size="small">
                <Close fontSize="small" />
              </CloseButton>
            </NotificationItem>
          </motion.div>
        ))}
      </AnimatePresence>
    </NotificationsContainer>
  );
};

export default FloatingNotifications; 